import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  ButtonProps,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionPlatform,
} from '@client/graphql/__generated__/types';
import {
  ContentScriptMessageType,
  sendMessageToContentScript,
} from '@common/extension';
import { Check, Clipboard, MessageCircle, NotepadText } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useCopyPaste } from '~/apps/consumer/hooks/useCopyPaste';
import { generateValidationErrorMessage } from '../utils/errorMessage.utils';

interface CopyValidationErrorsProps {
  document?: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment;
  validationData?: ExternalValidationReportDataFragment | null;
  userAddedIssues?: IngestionDocumentValidationErrorReviewFragment[];
  hiddenIssues?: IngestionDocumentValidationErrorReviewFragment[];
  platform?: IngestionPlatform;
  contractDocumentUuid?: string;
  // Button appearance
  label?: string;
  buttonProps?: Omit<ButtonProps, 'rightIcon' | 'onClick'>;
  // Add to message functionality
  onAddToMessage?: (
    document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment,
    contractDocumentUuid?: string
  ) => void;
  isAddedToMessage?: boolean;
  isDocumentReviewed?: boolean;
}

export const CopyValidationErrors: FC<CopyValidationErrorsProps> = ({
  label = 'Add to',
  buttonProps,
  document,
  validationData,
  userAddedIssues,
  hiddenIssues,
  platform,
  contractDocumentUuid,
  onAddToMessage,
  isAddedToMessage = false,
  isDocumentReviewed = false,
}) => {
  const { copyToClipboard } = useCopyPaste();
  const toast = useToast();

  const documentNoteLabel = useMemo(() => {
    if (platform === IngestionPlatform.DOTLOOP) {
      return 'Dotloop note';
    }
    if (platform === IngestionPlatform.SKYSLOPE) {
      return 'Comments';
    }

    return 'Document note';
  }, [platform]);

  const errorMessages = useMemo(() => {
    if (!document) return '';

    return generateValidationErrorMessage({
      document,
      validationData,
      userAddedIssues,
      hiddenIssues,
      includeDocumentName: platform !== IngestionPlatform.SKYSLOPE,
      useCompactFormat: platform === IngestionPlatform.SKYSLOPE,
    });
  }, [document, validationData, userAddedIssues, hiddenIssues, platform]);

  return (
    <Menu isLazy placement="bottom-end">
      <MenuButton
        as={Button}
        color="white"
        colorScheme="whiteAlpha"
        h="24px"
        px={2}
        py={1}
        rightIcon={<ChevronDownIcon />}
        size="xs"
        {...buttonProps}
      >
        {label}
      </MenuButton>
      <Portal>
        <MenuList
          bg="#000"
          borderColor="whiteAlpha.200"
          borderRadius="md"
          borderWidth="1px"
          boxShadow="0 0 14px rgba(0,0,0,0.25)"
          color="white"
          minW="186px"
          p={1}
        >
          <MenuItem
            _hover={{ bg: 'whiteAlpha.100' }}
            bg="transparent"
            borderRadius="md"
            py="8px"
            onClick={() => {
              copyToClipboard(errorMessages ?? '');
              toast({
                title: 'Errors copied to clipboard',
                status: 'success',
                duration: 1500,
              });
            }}
          >
            <HStack spacing="6px">
              <Icon as={Clipboard} boxSize={4} />
              <Text fontSize="md" fontWeight="normal">
                Clipboard
              </Text>
            </HStack>
          </MenuItem>
          <MenuItem
            _hover={{ bg: 'whiteAlpha.100' }}
            bg="transparent"
            borderRadius="md"
            py="8px"
            onClick={() =>
              void sendMessageToContentScript({
                type: ContentScriptMessageType.WRITE_DOCUMENT_NOTE,
                data: { text: errorMessages },
              })
            }
          >
            <HStack spacing="6px">
              <Icon as={NotepadText} boxSize={4} />
              <Text fontSize="md" fontWeight="normal">
                {documentNoteLabel}
              </Text>
            </HStack>
          </MenuItem>
          {onAddToMessage && document && (
            <MenuItem
              _hover={{ bg: 'whiteAlpha.100' }}
              bg="transparent"
              borderRadius="md"
              isDisabled={isAddedToMessage || isDocumentReviewed}
              py="8px"
              onClick={() => {
                onAddToMessage(document, contractDocumentUuid);
                toast({
                  title: 'Errors added to message',
                  description: 'Navigate to messaging tab to send',
                  status: 'success',
                  duration: 5000,
                });
              }}
            >
              <HStack spacing="6px">
                <Icon
                  as={
                    isAddedToMessage || isDocumentReviewed
                      ? Check
                      : MessageCircle
                  }
                  boxSize={4}
                />
                <Text fontSize="md" fontWeight="normal">
                  {isAddedToMessage || isDocumentReviewed
                    ? 'Added to Agent Notification'
                    : 'Agent Notification'}
                </Text>
              </HStack>
            </MenuItem>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};
